import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import {
  getErrorMessage,
  getPaginatedUrl,
  getPaginatedUrlWithSearchParams,
} from "store/helper";
import { crudService } from "services/crud.service";
import { initialDropdownDataState } from "store/constants";
import { NotificationManager } from "react-notifications";
import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";

const {
  contactEndPoint,
  projectEndPoint,
  sourceEndPoint,
  userEndPoint,
  inventoryEndPoint,
  individualPropertyEndPoint,
  allUserEndPoint,
} = endPoints;

export const fetchAllDropdownLeads = createAsyncThunk(
  "dropdownData/fetchAllDropdownLeads",
  async ({ page }: { page: number }, { rejectWithValue }) => {
    const paginatedUrl = getPaginatedUrl({
      pageNumber: page,
      endpoint: `${contactEndPoint}`,
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllDropdownProjects = createAsyncThunk(
  "dropdownData/fetchAllDropdownProjects",
  async ({ page }: { page: number }, { rejectWithValue }) => {
    const paginatedUrl = getPaginatedUrl({
      pageNumber: page,
      endpoint: `${projectEndPoint}`,
      limit: 500,
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllDropdownSource = createAsyncThunk(
  "dropdownData/fetchAllDropdownSource",
  async ({ page }: { page: number }, { rejectWithValue }) => {
    const paginatedUrl = getPaginatedUrl({
      pageNumber: page,
      endpoint: `${sourceEndPoint}`,
      limit: 500,
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllDropdownUser = createAsyncThunk(
  "dropdownData/fetchAllDropdownUser",
  async ({ page }: { page: number }, { rejectWithValue }) => {
    const paginatedUrl = getPaginatedUrl({
      pageNumber: page,
      endpoint: `${userEndPoint}`,
      limit: 500,
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllDropdownUserForShareLead = createAsyncThunk(
  "dropdownData/fetchAllDropdownUserForShareLead",
  async ({ page }: { page: number }, { rejectWithValue }) => {
    const paginatedUrl = getPaginatedUrl({
      pageNumber: page,
      endpoint: `${allUserEndPoint}`,
      limit: 500,
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllDropdownInventory = createAsyncThunk(
  "dropdownData/fetchAllDropdownInventory",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit: limit || 500,
      endpoint: inventoryEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllDropdownIndividualInventory = createAsyncThunk(
  "dropdownData/fetchAllDropdownIndividualInventory",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit: limit || 500,
      endpoint: individualPropertyEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const dropdownDataSlice = createSlice({
  name: "dropdownData",
  initialState: initialDropdownDataState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all leads
    builder
      .addCase(fetchAllDropdownLeads.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllDropdownLeads.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.data.leads = {
            meta: action.payload.meta,
            objects: action.payload.objects as any[],
          };
        }
      )
      .addCase(
        fetchAllDropdownLeads.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // fetch all projects
    builder
      .addCase(fetchAllDropdownProjects.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllDropdownProjects.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.data.projects = {
            meta: action.payload.meta,
            objects: action.payload.objects as any[],
          };
        }
      )
      .addCase(
        fetchAllDropdownProjects.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // fetch all source
    builder
      .addCase(fetchAllDropdownSource.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllDropdownSource.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.data.source = {
            meta: action.payload.meta,
            objects: action.payload.objects as any[],
          };
        }
      )
      .addCase(
        fetchAllDropdownSource.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // fetch all user
    builder
      .addCase(fetchAllDropdownUser.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllDropdownUser.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.data.user = {
            meta: action.payload.meta,
            objects: action.payload.objects as any[],
          };
        }
      )
      .addCase(
        fetchAllDropdownUser.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // fetch all users for share lead
    builder
      .addCase(fetchAllDropdownUserForShareLead.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllDropdownUserForShareLead.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.data["all-users"] = {
            meta: action.payload.meta,
            objects: action.payload.objects as any[],
          };
        }
      )
      .addCase(
        fetchAllDropdownUserForShareLead.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // fetch all property Unit
    builder
      .addCase(fetchAllDropdownInventory.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllDropdownInventory.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.data.property = {
            meta: action.payload.meta,
            objects: action.payload.objects as any[],
          };
        }
      )
      .addCase(
        fetchAllDropdownInventory.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // fetch all individual property Unit
    builder
      .addCase(fetchAllDropdownIndividualInventory.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllDropdownIndividualInventory.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.data["individual-property"] = {
            meta: action.payload.meta,
            objects: action.payload.objects as any[],
          };
        }
      )
      .addCase(
        fetchAllDropdownIndividualInventory.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const dropdownLeadReducer = dropdownDataSlice.reducer;
